import { normalizeEmail } from '@mahawi/eshop-common/dist/src/normalize';
import { useDispatch, useSelector } from 'react-redux';

import { type RootState } from '../reducers/customer';
import { isEmailAccepted } from '../reducers/email-validator/is-email-accepted';
import { emailValidatorVerify } from '../reducers/email-validator/reducer';
import { type EEmailValidatorCorrelationId } from '../reducers/email-validator/types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export enum EEmailVerificationStatus {
  Idle = 'idle',
  Checking = 'checking',
  Ok = 'ok',
  NotOk = 'notOk',
}

export interface IEmailVerification {
  onBlur: () => void;
  status: EEmailVerificationStatus;
  suggestedEmail: string | null;
}

export function useEmailVerification({
  correlationId,
  email,
  enabled,
}: {
  correlationId: EEmailValidatorCorrelationId;
  email: string;
  enabled: boolean;
}): IEmailVerification {
  const dispatch = useDispatch();
  const entry = useSelector(
    (state: RootState) => state.EmailValidator.entries[correlationId],
  );

  const onBlur = (): void => {
    if (!enabled || !EMAIL_PATTERN.test(email)) {
      return;
    }
    dispatch(emailValidatorVerify({ correlationId, email }));
  };

  if (
    !enabled ||
    !entry ||
    normalizeEmail(entry.email) !== normalizeEmail(email)
  ) {
    return {
      onBlur,
      status: EEmailVerificationStatus.Idle,
      suggestedEmail: null,
    };
  }

  if (entry.isLoading) {
    return {
      onBlur,
      status: EEmailVerificationStatus.Checking,
      suggestedEmail: null,
    };
  }

  if (entry.timedOut || !entry.result) {
    return {
      onBlur,
      status: EEmailVerificationStatus.Idle,
      suggestedEmail: null,
    };
  }

  return {
    onBlur,
    status: isEmailAccepted(entry.result)
      ? EEmailVerificationStatus.Ok
      : EEmailVerificationStatus.NotOk,
    suggestedEmail: entry.result.suggestedEmail,
  };
}
